import { formatPrice } from '../lib/format';
import type { GarmentSummary } from '../lib/types';

/**
 * A garment shown large, with its details captioned over the foot of the image.
 *
 * The layout follows what product-image research consistently lands on, and each
 * part is a decision rather than a style:
 *
 *  - **Caption over the image, not beneath it.** The image is the content; a
 *    detail block below it pushes the image up and, on a phone in a modal,
 *    off the top of the panel.
 *  - **Name bottom-left, price bottom-right.** Latin reading order puts the
 *    identifier where the eye lands first, and right-aligning the price gives it
 *    a consistent edge to scan down when several are seen in a row. Splitting
 *    them to opposite ends also stops a long name from displacing the price.
 *  - **Category as an eyebrow above the name**, smaller and lighter — it is
 *    context for the name, and reads as a label rather than competing with it.
 *  - **A gradient scrim, not a solid bar.** Garment photography is
 *    light-backgrounded often enough that white-on-image fails WCAG contrast
 *    somewhere in the catalogue; the scrim guarantees the floor without cropping
 *    the garment behind a hard band.
 *
 * `object-contain` rather than `cover`: this is the view someone opened to see
 * the whole garment, so nothing may be cropped — unlike the grid tile, where a
 * uniform aspect matters more than completeness.
 */
export function GarmentPreview({ garment }: { garment: GarmentSummary }) {
  const price = formatPrice(garment.price);

  if (!garment.displayImageUrl) {
    return (
      <div className="flex aspect-3/4 w-full flex-col items-center justify-center gap-1 rounded-md bg-subtle">
        <p className="text-sm text-tertiary">No image</p>
        <p className="text-base font-semibold text-primary">{garment.name}</p>
        <p className="text-sm text-secondary">{garment.category}</p>
        {price && <p className="text-base font-semibold text-primary">{price}</p>}
      </div>
    );
  }

  return (
    <figure className="relative overflow-hidden rounded-md bg-subtle">
      <img
        src={garment.displayImageUrl}
        alt={garment.name}
        className="max-h-[60dvh] w-full object-contain"
      />

      <figcaption
        className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 px-3 pt-10 pb-3"
        style={{
          // Tailwind's gradient utilities would do this, but the explicit stops
          // are what keep the midpoint from washing out a light garment.
          backgroundImage:
            'linear-gradient(to top, rgb(0 0 0 / 0.78) 0%, rgb(0 0 0 / 0.45) 45%, rgb(0 0 0 / 0) 100%)',
        }}
      >
        <div className="min-w-0">
          <span className="block truncate text-[11px] font-medium tracking-wide text-white/75 uppercase">
            {garment.category}
          </span>
          <span className="block truncate text-base font-semibold text-white">{garment.name}</span>
        </div>
        {price && (
          <span className="shrink-0 text-base font-semibold text-white tabular-nums">{price}</span>
        )}
      </figcaption>
    </figure>
  );
}
